import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withFormsy } from 'formsy-react';
import { Button, Grid, Typography, useTheme } from '@mui/material';

import { updateUserSettings } from 'app/store/userSlice';
import Icon from '../../Icon';
import useSettings from '../../hooks/useSettings';
import useUserData from '../../hooks/useUserData';
import BaseTextField, { type FieldProps } from '../components/BaseTextField';
import ResponsiveDialog from '../../dialogs/ResponsiveDialog';

interface ThemePreviewProps {
	id: string;
	theme: any;
	onSelect: (id: string) => void;
}

type AppSettingsFieldProps = {
	/** The user's uid */
	userUid: string;
} & Omit<FieldProps<string>, 'startAdornment' | 'endAdornment'>;

function ThemePreview({ id, theme, onSelect }: ThemePreviewProps) {
	const { t } = useSettings();
	const _theme = useTheme();
	const primaryColor = theme.palette.primary[500] ? theme.palette.primary[500] : theme.palette.primary.main;
	const primaryColorContrast = _theme.palette.getContrastText(primaryColor);
	const secondaryColor = theme.palette.secondary[500] ? theme.palette.secondary[500] : theme.palette.secondary.main;
	const secondaryColorContrast = _theme.palette.getContrastText(secondaryColor);
	const backgroundColor = theme.palette.background.default;
	const backgroundColorContrast = _theme.palette.getContrastText(theme.palette.background.default);
	const paperColor = theme.palette.background.paper;
	const paperColorContrast = _theme.palette.getContrastText(theme.palette.background.paper);

	return (
		<div className="mb-8">
			<button
				className="relative w-full cursor-pointer overflow-hidden rounded-6 text-left font-500 shadow-1 hover:shadow-2"
				style={{ backgroundColor, color: backgroundColorContrast }}
				onClick={() => onSelect(id)}
				type="button"
			>
				<div
					className="relative h-56 w-full px-8 pt-8"
					style={{ backgroundColor: primaryColor, color: primaryColorContrast }}
				>
					<span className="text-12 opacity-75">{t('THEME_HEADER')}</span>
					<div
						className="absolute bottom-0 right-0 -mb-10 mr-4 flex h-20 w-20 items-center justify-center rounded-full text-10 shadow-1"
						style={{ backgroundColor: secondaryColor, color: secondaryColorContrast }}
					>
						<span className="opacity-75">S</span>
					</div>
				</div>
				<div className="-mt-24 w-full pl-8 pr-28">
					<div
						className="relative h-96 w-full rounded-4 p-8 shadow-1"
						style={{ backgroundColor: paperColor, color: paperColorContrast }}
					>
						<span className="text-12 opacity-75">{t('THEME_PAPER')}</span>
					</div>
				</div>
				<div className="w-full px-8 py-8">
					<span className="text-12 opacity-75">{t('THEME_BACKGROUND')}</span>
				</div>
			</button>
			<Typography className="mt-12 text-center font-500">{id}</Typography>
		</div>
	);
}

function ThemeSelectField({
	className,
	name,
	value,
	userUid,
	sendChange,
	isEdit,
	isFormSubmitted,
	isPristine,
	isValid
}: AppSettingsFieldProps) {
	const dispatch = useDispatch<any>();
	const { t } = useSettings();
	const userData = useUserData();
	const themes = useSelector(({ fuse }: any) => fuse.settings.themes);

	const [dialogOpen, setDialogOpen] = React.useState(false);

	function switchDialogOpen() {
		if (isEdit) setDialogOpen(!dialogOpen);
	}

	function handleChange(id) {
		sendChange(name, id);
		if (userUid === userData.user_uid) {
			dispatch(updateUserSettings({ theme: id }));
			switchDialogOpen();
		}
	}

	return (
		<>
			<BaseTextField
				className={className}
				name={name}
				label={t('THEME_FIELD')}
				isEdit={isEdit}
				startAdornment={<Icon icon="palette-outline" />}
				value={value || t('DEFAULT_VALUE')}
				isFormSubmitted={isFormSubmitted}
				isPristine={isPristine}
				isValid={isValid}
				onClick={switchDialogOpen}
			/>
			<ResponsiveDialog
				open={dialogOpen}
				onClose={switchDialogOpen}
				maxWidth="md"
				title={t('THEME_FIELD')}
				content={
					<Grid container spacing={2}>
						{Object.entries(themes)
							.filter(([key, _value]) => !(key === 'mainThemeDark' || key === 'mainThemeLight'))
							.map(([key, theme]) => (
								<Grid key={key} size={{ lg: 2, md: 3, sm: 3, xs: 6 }}>
									<ThemePreview id={key} theme={theme} onSelect={handleChange} />
								</Grid>
							))}
					</Grid>
				}
				actions={<Button onClick={switchDialogOpen}>{t('DIALOG_ACTION_BACK')}</Button>}
			/>
		</>
	);
}

export default withFormsy(ThemeSelectField);
